import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CategoryRow {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  image: string | null;
  color: string | null;
  icon?: string | null;
}

/**
 * Fetch categories from DB. The admin-configured categories table is the
 * single source of truth for slug, name, color, image — there
 * is no client-side placeholder list with hard-coded values. During the
 * initial fetch, consumers see `loading=true` and render skeletons.
 */
export function useCategories() {
  const { data: categories = [] as CategoryRow[], isLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("categories")
        .select("id,name,slug,description,image,color,icon")
        .order("name", { ascending: true });

      if (error) throw error;
      // Return real DB rows; empty array is valid.
      return (data || []) as CategoryRow[];
    },
    staleTime: 1000 * 60 * 60, // Cache 1 hour — categories change rarely
  });

  return { categories, loading: isLoading };
}
